import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSostaStyles } from "./useSostaStyles";
import Footer from "./Footer";

const TOPIC_OPTIONS = [
  "Account issue",
  "Subscription / billing",
  "Cancel or delete account",
  "Bug or technical issue",
  "Feedback or suggestion",
  "Something else",
];

export default function SupportPage() {
  const [scrolled, setScrolled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useSostaStyles();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Support — Sosta";
    return () => {
      document.title = "Sosta";
    };
  }, []);

  function handleSubmit(e) {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !topic || !message.trim()) {
      window.alert("Please fill in all fields before sending.");
      return;
    }

    if (!email.includes("@")) {
      window.alert("Please enter a valid email address.");
      return;
    }

    const mailtoLink = `mailto:hello@sosta.ai?subject=${encodeURIComponent(`Support: ${topic} - ${name}`)}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\nTopic: ${topic}\n\nMessage:\n${message}`)}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  }

  return (
    <>
      <nav className={`s-nav${scrolled ? " scrolled" : ""}`}>
        <Link to="/" className="s-logo" style={{ textDecoration: "none" }}>
          Sosta<span className="s-logo-dot" />
        </Link>
        <Link to="/" className="s-nav-btn">← Back to Sosta</Link>
      </nav>

      <div className="s-support-hero">
        <h1>We've got you, <em>babe.</em></h1>
        <p>Need help with your account or the app? We're here for it.</p>
      </div>

      <div className="s-support-container">
        <div className="s-support-section">
          <h2 className="s-section-title">📧 Email Support</h2>
          <a href="mailto:hello@sosta.ai" className="s-email-card">
            <div className="s-email-icon">🩷</div>
            <div>
              <h3>hello@sosta.ai</h3>
              <p>Send us a message and we'll get back to you within 24–48 hours.</p>
            </div>
          </a>
        </div>

        <div className="s-support-section">
          <h2 className="s-section-title">❌ How to Cancel Your Subscription</h2>
          <div className="s-howto-card">
            <h3>📱 On iPhone</h3>
            <ol>
              <li>Open the <strong>Settings</strong> app on your iPhone</li>
              <li>Tap your name at the top → <strong>Subscriptions</strong></li>
              <li>Find <strong>Sosta</strong> in the list and tap it</li>
              <li>Tap <strong>Cancel Subscription</strong></li>
              <li>Confirm cancellation</li>
            </ol>
            <div className="s-howto-note">
              Your Sosta Plus access continues until the end of the current billing period. You won't be charged again after cancellation. Subscriptions must be cancelled at least 24 hours before the next renewal date to avoid being charged for the next period.
            </div>
          </div>
        </div>

        <div className="s-support-section">
          <h2 className="s-section-title">🗑️ How to Delete Your Account</h2>
          <div className="s-howto-card">
            <h3>🩷 Delete from within the app</h3>
            <ol>
              <li>Open the <strong>Sosta</strong> app</li>
              <li>Go to your <strong>Profile</strong> tab</li>
              <li>Scroll down and tap <strong>Settings</strong></li>
              <li>Tap <strong>Delete Account</strong></li>
              <li>Confirm deletion</li>
            </ol>
            <div className="s-howto-note">
              Deleting your account is permanent and cannot be undone. All your data including chat history, no-contact tracker, and community posts will be permanently removed.
            </div>
          </div>
          <div className="s-howto-card">
            <h3>📧 Request deletion by email</h3>
            <ol>
              <li>Email us at <strong>hello@sosta.ai</strong></li>
              <li>Use the subject line: <strong>"Account Deletion Request"</strong></li>
              <li>Include the email address associated with your account</li>
              <li>We'll confirm deletion within 5 business days</li>
            </ol>
            <div className="s-howto-note">
              Please cancel your subscription before requesting account deletion to avoid future charges.
            </div>
          </div>
        </div>

        <div className="s-support-section">
          <h2 className="s-section-title">💬 Contact Us</h2>
          {!submitted ? (
            <form className="s-contact-form" onSubmit={handleSubmit}>
              <div className="s-form-row">
                <div className="s-form-group">
                  <label htmlFor="name">Your name</label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Bailey"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="s-form-group">
                  <label htmlFor="email">Your email</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="bailey@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="s-form-group">
                <label htmlFor="topic">What do you need help with?</label>
                <select
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                >
                  <option value="" disabled>Select a topic</option>
                  {TOPIC_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="s-form-group">
                <label htmlFor="message">Tell us more</label>
                <textarea
                  id="message"
                  rows={5}
                  placeholder="Describe your issue or question..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <button type="submit" className="s-btn-submit">Send message 🩷</button>
            </form>
          ) : (
            <div className="s-form-success">
              🩷 Message sent! We'll get back to you at your email within 24–48 hours.
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}
